import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, CheckCircle2, Circle, FileCheck2 } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatDate } from "@/lib/format"
import { cn } from "@/lib/utils"

import { Advertencias } from "@/routes/holding/Advertencias"
import { fetchMapaDelGrupo, type MapaDePais, type NivelObligacion } from "@/routes/holding/api"
import { useFiltrosGrupo } from "@/routes/holding/HoldingLayout"
import { PagoDeImpuestos } from "@/routes/holding/PagoDeImpuestos"
import { t } from "@/lib/i18n"

/**
 * EL MAPA DE OBLIGACIONES: que debe el grupo, ante quien y cuando.
 *
 * LA COLUMNA QUE IMPORTA ES «MARTY». No dice «soportado: si/no» —eso no lo sabe
 * medir nadie— sino cual de tres cosas distintas hace el sistema con cada
 * obligacion:
 *
 *   la conozco    se cuando vence y en que norma se apoya.
 *   la preparo    ademas se ARMAR el entregable.
 *   la presento   ademas se ENTREGARLO ante el fisco.
 *
 * El tercero es CERO en los cuatro paises, y decirlo en la pantalla es la
 * diferencia entre un mapa y una promesa: presentar exige certificado, firma
 * electronica y un canal con cada autoridad. Mientras eso no exista, el sistema
 * es visibilidad y preparacion.
 *
 * Quien lea esta pantalla tiene que salir sabiendo QUE VA A TENER QUE HACER A
 * MANO este mes. Ese es el unico objetivo.
 */

const NIVEL: Record<NivelObligacion, { label: string; icono: typeof Circle; clase: string }> = {
  la_conozco: { label: "La conozco", icono: Circle, clase: "text-muted-foreground" },
  la_preparo: { label: "La preparo", icono: FileCheck2, clase: "text-success" },
  la_presento: { label: "La presento", icono: CheckCircle2, clase: "text-success" },
}

const TIPO: Record<string, string> = {
  TAX_RETURN: "Declaración",
  TAX_PAYMENT: "Pago",
  ELECTRONIC_BOOK: "Libro electrónico",
  INFORMATIVE: "Informativa",
  PAYROLL_FILING: "Planilla",
  PAYROLL_PAYMENT: "Pago de planilla",
  STATUTORY_DEPOSIT: "Depósito legal",
}

export function ObligacionesPage() {
  const { periodo } = useFiltrosGrupo()
  const q = useQuery({
    queryKey: ["obligaciones", periodo],
    queryFn: () => fetchMapaDelGrupo(periodo),
  })
  const d = q.data

  /*
    EL AÑO SALE DEL PERÍODO DE LA BARRA, que es un MES («2026-10»).

    El pago de impuestos va por año, trimestre y mes; el mapa de obligaciones va
    por mes. Compartir el mando es lo correcto —son la misma pestaña— y lo que no
    se puede es poner un segundo selector de año al lado: es justo lo que se
    quitó de `HoldingLayout` cuando había dos períodos diciendo cosas distintas.
  */
  const anio = Number(periodo.slice(0, 4))

  return (
    <div className="space-y-3">
      {/*
        EL DINERO VA PRIMERO. Cesar, punto 13: la pestaña se llamaba
        «Obligaciones» y contestaba cuándo y ante quién, nunca cuánto. El mapa se
        queda debajo porque sigue siendo lo único que avisa de una presentación
        que nadie preparó — un vencimiento no tiene importe hasta que se calcula.
      */}
      <PagoDeImpuestos anio={anio} />

      {d ? (
        <>
          {/* El titular, que es un recuento y no una lista. */}
          <h2 className="pt-1 text-sm font-medium leading-tight text-foreground">
            {t("Qué hay que presentar, y ante quién")}
          </h2>
          <div className="grid gap-3 md:grid-cols-3">
            <Recuento
              etiqueta={t("Obligaciones del período")}
              valor={d.total_obligaciones}
              nota={t("De las cuatro sociedades juntas, con el régimen que cada una declara.")}
            />
            <Recuento
              etiqueta={t("Marty sabe armarlas")}
              valor={d.se_preparan}
              nota={t("El entregable sale del sistema. El resto se prepara fuera.")}
            />
            <Recuento
              etiqueta={t("Marty sabe presentarlas")}
              valor={d.se_presentan}
              enRojo={d.se_presentan === 0}
              nota={t("Presentar exige certificado, firma electrónica y canal con cada fisco.")}
            />
          </div>

          <div className="rounded-md border bg-surface-sunken px-3 py-2">
            <p className="max-w-[110ch] text-xs leading-[1.2rem] text-muted-foreground">
              {d.nota}
            </p>
          </div>
        </>
      ) : null}

      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && (d?.sociedades.length ?? 0) === 0}
        emptyMessage={t("No hay sociedades con normativa cargada.")}
        emptyHint={t("Marty conoce las obligaciones de Perú, México, Brasil y Panamá. Una sociedad de otro país aparece aquí sin obligaciones hasta que exista su rulepack.")}
        onRetry={() => void q.refetch()}
        rows={6}
        columns={[10, 20, 26, 14, 14, 16]}
      >
        {d?.sociedades.map((s) => <PorSociedad key={s.entity_id ?? s.pais} s={s} />)}
      </DataState>

      {d && d.sin_rulepack.length > 0 ? (
        <Advertencias
          advertencias={[
            {
              clave: "sin_rulepack",
              grave: true,
              texto: `${d.sin_rulepack.join(", ")} no tiene normativa cargada: Marty no sabe qué debe ni cuándo. Sus obligaciones NO están en este recuento.`,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

function PorSociedad({ s }: { s: MapaDePais }) {
  return (
    <div className="space-y-2">
      <Advertencias advertencias={s.advertencias} de={s.razon_social ?? s.pais} />
      <WorkPanel
        title={`${s.razon_social ?? s.pais} · ${s.autoridad}`}
        meta={
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="code">
              {s.pais}
            </Badge>
            <Badge variant={s.regimen_declarado ? "outline" : "warning-soft"} estado>
              {s.regimen_evaluado}
            </Badge>
            <Badge
              variant={s.madurez === "COMPLETO" ? "success-soft" : "warning-soft"}
              estado
              title={t("COMPLETO = con formularios. MODELO = normativa y calendario, sin formularios.")}
            >
              {s.madurez.replace(/_/g, " ").toLowerCase()}
            </Badge>
            <span className="code text-2xs text-muted-foreground">
              rulepack {s.version_rulepack}
            </span>
          </span>
        }
        footnote={
          s.pendientes_de_verificar > 0
            ? `${s.pendientes_de_verificar} regla(s) de este país están marcadas por el propio sistema como no cotejadas contra fuente primaria. No es una métrica de calidad: es lo que falta verificar antes de firmar algo que dependa de ellas.`
            : undefined
        }
      >
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[9rem]">{t("Código")}</TableHead>
              <TableHead className="w-[9rem]">{t("Tipo")}</TableHead>
              <TableHead>{t("Obligación")}</TableHead>
              <TableHead className="w-24">{t("Vence")}</TableHead>
              <TableHead className="w-24">{t("Sale de caja")}</TableHead>
              <TableHead className="w-[8.5rem]">{t("Marty")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/*
              SIN FILAS NO SE DEJA LA TABLA MUDA. Pasa cuando el rulepack de ese
              país falló: la advertencia de arriba lo dice, pero una tabla con
              cabeceras y nada debajo se lee como un componente roto, y sobre
              todo se lee como «no debe nada este mes» — la interpretación más
              cara posible de un fallo técnico.
            */}
            {s.obligaciones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-xs text-muted-foreground">
                  {s.error
                    ? "El mapa de esta sociedad no se pudo calcular. La lista vacía no significa que no deba nada."
                    : "Ninguna obligación para este período con el régimen y los empleados evaluados."}
                </TableCell>
              </TableRow>
            ) : null}
            {s.obligaciones.map((o) => {
              const n = NIVEL[o.nivel]
              const Icono = n.icono
              return (
                <TableRow key={o.codigo}>
                  <TableCell className="code truncate font-medium" title={o.codigo}>
                    {o.codigo}
                  </TableCell>
                  <TableCell className="truncate text-muted-foreground">
                    {TIPO[o.tipo] ?? o.tipo}
                  </TableCell>
                  <TableCell className="truncate" title={o.fundamento_legal ?? o.nombre}>
                    {o.nombre}
                  </TableCell>
                  <TableCell className="code text-muted-foreground">
                    {o.vence ? formatDate(o.vence) : "—"}
                  </TableCell>
                  <TableCell className="code text-muted-foreground">
                    {o.sale_de_caja ? formatDate(o.sale_de_caja) : "—"}
                  </TableCell>
                  <TableCell>
                    <span className={cn("inline-flex items-center gap-1.5 text-2xs", n.clase)}>
                      <Icono className="h-3 w-3 shrink-0" />
                      {n.label}
                    </span>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </WorkPanel>
    </div>
  )
}

function Recuento({
  etiqueta,
  valor,
  nota,
  enRojo,
}: {
  etiqueta: string
  valor: number
  nota: string
  enRojo?: boolean
}) {
  return (
    <div className="panel px-4 py-3">
      <div className="eyebrow">{etiqueta}</div>
      {/* La UNICA cifra en rojo de la vista: la que requiere atencion. */}
      <div className={cn("mt-1 text-xl", enRojo ? "cifra-atencion" : "cifra")}>
        {valor}
        {enRojo ? (
          <AlertTriangle className="ml-2 inline h-3.5 w-3.5 align-baseline text-margen" />
        ) : null}
      </div>
      <p className="mt-1 text-2xs leading-[0.95rem] text-muted-foreground">{nota}</p>
    </div>
  )
}
